import type { HttpClient } from '@/lib/http/HttpClient';
import type {
  MachineReadAllRequest,
  MachineDeleteRequest,
  ToolDeleteByReferenceRequest,
  ToolDeleteRequest,
  MachineReadRequest,
  MachineWriteRequest,
  MaterialReadAllRequest,
  MaterialReadRequest,
  MaterialWriteRequest,
  MaterialLotFindOrCreateRequest,
  MaterialLotReadForMaterialRequest,
  StatusRequest,
  ToolReadRequest,
  ToolWriteRequest,
  TraceReadRequest,
  TraceWriteRequest,
  ArticleReadRequest,
  ArticleReadAllRequest,
  ArticleWriteRequest,
  ArticleDeleteRequest,
  ProcessReadRequest,
  ProcessReadAllRequest,
  ProcessReadByToolRequest,
  ProcessWriteRequest,
  ProcessDeleteRequest,
} from './TraceabilityRequestType';
import type {
  AllToolsResponse,
  ToolSummariesResponse,
  DeleteToolsByReferenceResponse,
  MachineAllResponse,
  MachineResponse,
  MachineWriteResponse,
  MaterialResponse,
  MaterialAllResponse,
  MaterialWriteResponse,
  MaterialLotResponse,
  MaterialLotListResponse,
  ToolResponse,
  TraceResponse,
  ArticleResponse,
  ArticleAllResponse,
  ArticleWriteResponse,
  ProcessResponse,
  ProcessAllResponse,
  ProcessWriteResponse,
} from './TraceabilityResponseType';

export class TraceabilityApiClient {
  private readonly http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  async toolRead(data: ToolReadRequest, options?: RequestInit) {
    return this.http.post<ToolResponse>('/api/tracabilite/tool/read', data, options);
  }

  async toolReadAll(companyName?: string, options?: RequestInit) {
    const body = companyName ? { companyName } : {};
    return this.http.post<AllToolsResponse>('/api/tracabilite/tool/readAll', body, options);
  }

  /** Every tool of the caller's company without its traces, for the tool and reference pickers. */
  async toolReadSummaries(options?: RequestInit) {
    return this.http.post<ToolSummariesResponse>(
      '/api/tracabilite/tool/readSummaries',
      {},
      options
    );
  }

  async toolSummariesStatus(data: StatusRequest, options?: RequestInit) {
    return this.http.post<ToolSummariesResponse>('/api/tracabilite/job/status', data, options);
  }

  async toolwrite(data: ToolWriteRequest, options?: RequestInit) {
    return this.http.post<ToolResponse>('/api/tracabilite/tool/write', data, options);
  }

  /**
   * Both `toolStatus` and `toolsStatus` POST to the shared job-status endpoint
   * (`/api/tracabilite/job/status`) but return different response shapes:
   * - `toolStatus` returns a single `ToolResponse` (one tool updated).
   * - `toolsStatus` returns `AllToolsResponse` (bulk update — the payload
   *   must include the appropriate identifiers for multiple tools).
   * The caller is responsible for choosing the right method based on the
   * expected response shape.
   */
  async toolStatus(data: StatusRequest, options?: RequestInit) {
    return this.http.post<ToolResponse>('/api/tracabilite/job/status', data, options);
  }

  async toolsStatus(data: StatusRequest, options?: RequestInit) {
    return this.http.post<AllToolsResponse>('/api/tracabilite/job/status', data, options);
  }

  async traceRead(data: TraceReadRequest, options?: RequestInit) {
    return this.http.post<TraceResponse>('/api/tracabilite/trace/read', data, options);
  }

  async tracewrite(data: TraceWriteRequest, options?: RequestInit) {
    return this.http.post<TraceResponse>('/api/tracabilite/trace/write', data, options);
  }

  async traceStatus(data: StatusRequest, options?: RequestInit) {
    return this.http.post<TraceResponse>('/api/tracabilite/job/status', data, options);
  }

  async materialRead(data: MaterialReadRequest, options?: RequestInit) {
    return this.http.post<MaterialResponse>('/api/tracabilite/material/read', data, options);
  }

  async materialReadAll(data: MaterialReadAllRequest, options?: RequestInit) {
    return this.http.post<MaterialAllResponse>('/api/tracabilite/material/readAll', data, options);
  }

  async materialWrite(data: MaterialWriteRequest, options?: RequestInit) {
    return this.http.post<MaterialWriteResponse>('/api/tracabilite/material/write', data, options);
  }

  async materialStatus(data: StatusRequest, options?: RequestInit) {
    return this.http.post<MaterialResponse>('/api/tracabilite/job/status', data, options);
  }

  async materialLotFindOrCreate(data: MaterialLotFindOrCreateRequest, options?: RequestInit) {
    return this.http.post<MaterialLotResponse>(
      '/api/tracabilite/material-lot/findOrCreate',
      data,
      options
    );
  }

  async materialLotReadForMaterial(data: MaterialLotReadForMaterialRequest, options?: RequestInit) {
    return this.http.post<MaterialLotListResponse>(
      '/api/tracabilite/material-lot/readForMaterial',
      data,
      options
    );
  }

  async machineRead(data: MachineReadRequest, options?: RequestInit) {
    return this.http.post<MachineResponse>('/api/tracabilite/machine/read', data, options);
  }

  async machineReadAll(data: MachineReadAllRequest, options?: RequestInit) {
    return this.http.post<MachineAllResponse>('/api/tracabilite/machine/readAll', data, options);
  }

  async machineWrite(data: MachineWriteRequest, options?: RequestInit) {
    return this.http.post<MachineWriteResponse>('/api/tracabilite/machine/write', data, options);
  }

  async machineStatus(data: StatusRequest, options?: RequestInit) {
    return this.http.post<MachineResponse>('/api/tracabilite/job/status', data, options);
  }

  async machineDelete(data: MachineDeleteRequest, options?: RequestInit) {
    return this.http.post<MachineResponse>('/api/tracabilite/machine/delete', data, options);
  }

  async toolDelete(data: ToolDeleteRequest, options?: RequestInit) {
    return this.http.post<ToolResponse>('/api/tracabilite/tool/delete', data, options);
  }

  /**
   * Deletes every tool of the caller's company carrying the reference (traces
   * cascade) plus the reference alias. Admin-only route (orchestrator >= 0.11.2).
   */
  async toolDeleteByReference(data: ToolDeleteByReferenceRequest, options?: RequestInit) {
    return this.http.post<DeleteToolsByReferenceResponse>(
      '/api/tracabilite/tool/deleteByReference',
      data,
      options
    );
  }

  async articleRead(data: ArticleReadRequest, options?: RequestInit) {
    return this.http.post<ArticleResponse>('/api/tracabilite/article/read', data, options);
  }

  async articleReadAll(data: ArticleReadAllRequest, options?: RequestInit) {
    return this.http.post<ArticleAllResponse>('/api/tracabilite/article/readAll', data, options);
  }

  async articleWrite(data: ArticleWriteRequest, options?: RequestInit) {
    return this.http.post<ArticleWriteResponse>('/api/tracabilite/article/write', data, options);
  }

  async articleDelete(data: ArticleDeleteRequest, options?: RequestInit) {
    return this.http.post<ArticleResponse>('/api/tracabilite/article/delete', data, options);
  }

  async processRead(data: ProcessReadRequest, options?: RequestInit) {
    return this.http.post<ProcessResponse>('/api/tracabilite/process/read', data, options);
  }

  async processReadAll(data: ProcessReadAllRequest, options?: RequestInit) {
    return this.http.post<ProcessAllResponse>('/api/tracabilite/process/readAll', data, options);
  }

  async processReadByTool(data: ProcessReadByToolRequest, options?: RequestInit) {
    return this.http.post<ProcessAllResponse>('/api/tracabilite/process/readByTool', data, options);
  }

  async processWrite(data: ProcessWriteRequest, options?: RequestInit) {
    return this.http.post<ProcessWriteResponse>('/api/tracabilite/process/write', data, options);
  }

  async processDelete(data: ProcessDeleteRequest, options?: RequestInit) {
    return this.http.post<ProcessResponse>('/api/tracabilite/process/delete', data, options);
  }
}
